// ============================================================================
// FLEET MANAGEMENT PAGE - Admin-only device fleet management
// Top-level page for health monitoring, config updates, software deployment
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listDevicesViaN8n,
  DeviceInfo,
  isGlassesDevice,
  getGlassesMetrics,
} from '../services/n8n';
import {
  requestHealthReport,
  restartDevice,
  pushConfigUpdate,
  deployUpdate,
  getUploadPackageUrl,
  getManagementCommands,
  ManagementCommand,
  formatUptime,
  getMetricColor,
  getTempColor
} from '../services/deviceManagement';
import {
  Monitor,
  Cpu,
  HardDrive,
  Thermometer,
  RotateCcw,
  Activity,
  Settings,
  RefreshCw,
  Upload,
  Loader,
  X,
  ChevronDown,
  ChevronRight,
  Server,
  Wifi,
  Clock,
  Package,
  Key,
  Terminal,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Search,
  ChevronLeft,
  Glasses,
} from 'lucide-react';
import { ConfirmationModal } from '../components/ConfirmationModal';

// ============================================================================
// TYPES
// ============================================================================

type SortCol = 'status' | 'deviceId' | 'deviceType' | 'locationId' | 'uptime' | 'cpu' | 'mem';
type SortDir = 'asc' | 'desc';
interface SortState { col: SortCol; dir: SortDir }
interface Filters { search: string; status: string; deviceType: string; transport: string }

const PAGE_SIZES = [10, 25, 50];

// ============================================================================
// HELPERS
// ============================================================================

function isDeviceOnline(device: DeviceInfo): boolean {
  if (!device.lastHeartbeat) return false;
  return Date.now() - new Date(device.lastHeartbeat).getTime() < 90000;
}

function applyFilters(devices: DeviceInfo[], f: Filters): DeviceInfo[] {
  return devices.filter(d => {
    if (f.status === 'online' && !isDeviceOnline(d)) return false;
    if (f.status === 'offline' && isDeviceOnline(d)) return false;
    if (f.deviceType && d.deviceType !== f.deviceType) return false;
    if (f.transport && (d.transport ?? 'n8n') !== f.transport) return false;
    if (f.search) {
      const q = f.search.toLowerCase();
      if (!d.deviceId.toLowerCase().includes(q) && !d.locationId.toLowerCase().includes(q)) return false;
    }
    return true;
  });
}

function applySort(devices: DeviceInfo[], sort: SortState | null): DeviceInfo[] {
  if (!sort) return devices;
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...devices].sort((a, b) => {
    switch (sort.col) {
      case 'status':
        return mult * (Number(isDeviceOnline(b)) - Number(isDeviceOnline(a)));
      case 'deviceId':
        return mult * a.deviceId.localeCompare(b.deviceId);
      case 'deviceType':
        return mult * a.deviceType.localeCompare(b.deviceType);
      case 'locationId':
        return mult * a.locationId.localeCompare(b.locationId);
      case 'uptime': {
        const au = a.systemMetrics?.uptimeSeconds ?? -1;
        const bu = b.systemMetrics?.uptimeSeconds ?? -1;
        return mult * (au - bu);
      }
      case 'cpu': {
        const ac = a.systemMetrics?.cpuPercent ?? -1;
        const bc = b.systemMetrics?.cpuPercent ?? -1;
        return mult * (ac - bc);
      }
      case 'mem': {
        const am = a.systemMetrics?.memPercent ?? -1;
        const bm = b.systemMetrics?.memPercent ?? -1;
        return mult * (am - bm);
      }
      default: return 0;
    }
  });
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SortIcon({ col, sort }: { col: SortCol; sort: SortState | null }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
  return sort.dir === 'asc'
    ? <ArrowUp className="w-3.5 h-3.5 text-primary" />
    : <ArrowDown className="w-3.5 h-3.5 text-primary" />;
}

function ColHeader({
  col, label, sort, onSort
}: { col: SortCol; label: string; sort: SortState | null; onSort: (col: SortCol) => void }) {
  return (
    <th
      className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-slate-600 transition-colors whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      <div className="flex items-center gap-1.5">
        {label}
        <SortIcon col={col} sort={sort} />
      </div>
    </th>
  );
}

function MetricCard({ label, value, unit, icon: Icon, color }: {
  label: string;
  value?: number;
  unit: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-white rounded-lg p-3 border border-[#e2e8f0]">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-slate-400" />
        <span className="text-xs text-slate-400 font-medium">{label}</span>
      </div>
      <span className={`text-lg font-bold ${value != null && value >= 0 ? color : 'text-slate-300'}`}>
        {value != null && value >= 0 ? `${value}${unit}` : 'N/A'}
      </span>
    </div>
  );
}

function CommandTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'health-report': return <Activity size={12} className="text-emerald-500" />;
    case 'update-config': return <Settings size={12} className="text-amber-500" />;
    case 'update-software': return <Package size={12} className="text-indigo-500" />;
    case 'rotate-ssh-keys': return <Key size={12} className="text-purple-500" />;
    case 'restart-service': return <RotateCcw size={12} className="text-red-500" />;
    default: return <Terminal size={12} className="text-slate-400" />;
  }
}

function CommandStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 size={12} /> Done</span>;
    case 'error':
      return <span className="flex items-center gap-1 text-red-600"><XCircle size={12} /> Error</span>;
    case 'pending':
      return <span className="flex items-center gap-1 text-amber-600"><Clock size={12} /> Pending</span>;
    default:
      return <span className="text-slate-400">{status}</span>;
  }
}

function renderCommandHistory(isLoadingHistory: boolean, commands: ManagementCommand[]): React.ReactNode {
  if (isLoadingHistory) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
        <Loader size={12} className="animate-spin" /> Loading...
      </div>
    );
  }
  if (commands.length === 0) {
    return <p className="text-xs text-slate-400 py-2">No command history</p>;
  }
  return (
    <div className="space-y-1">
      {commands.slice(0, 5).map(cmd => (
        <div key={cmd.commandId} className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-white border border-slate-100">
          <div className="flex items-center gap-2">
            <CommandTypeIcon type={cmd.type} />
            <span className="font-medium text-slate-700">{cmd.type}</span>
            <span className="text-slate-400 font-mono">{cmd.commandId.slice(0, 12)}...</span>
          </div>
          <div className="flex items-center gap-2">
            <CommandStatusBadge status={cmd.status} />
            <span className="text-slate-400">{new Date(cmd.queuedAt).toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

type DeviceMetrics = DeviceInfo['systemMetrics'];

function DeviceIdentityCells({ device, metrics }: { device: DeviceInfo; metrics: DeviceMetrics }) {
  return (
    <>
      {/* Device ID */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="text-sm font-bold text-slate-900 font-mono">{device.deviceId}</span>
        {isGlassesDevice(device) && getGlassesMetrics(device).ownerEmail && (
          <div className="text-xs text-slate-400">{getGlassesMetrics(device).ownerEmail}</div>
        )}
      </td>

      {/* Type */}
      <td className="px-4 py-3 whitespace-nowrap">
        {isGlassesDevice(device) ? (
          <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
            <Glasses size={11} />
            {device.deviceType}
          </span>
        ) : (
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-medium">
            {device.deviceType}
          </span>
        )}
        <span
          title={device.transport === 'iot' ? 'Reports over AWS IoT Core' : device.transport === 'backend' ? 'Registered by the platform for a facilitator' : 'Reports through n8n'}
          className={`ml-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
            device.transport === 'iot'
              ? 'bg-primary/10 text-primary border-primary/30'
              : device.transport === 'backend'
                ? 'bg-purple-50 text-purple-700 border-purple-200'
                : 'bg-slate-50 text-slate-400 border-slate-200'
          }`}
        >
          {device.transport === 'iot' ? 'IoT' : device.transport === 'backend' ? 'Backend' : 'n8n'}
        </span>
      </td>

      {/* Role */}
      <td className="px-4 py-3 whitespace-nowrap">
        {device.assignedRole ? (
          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-medium">
            {device.assignedRole}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
        {device.transport === 'backend' && device.ownerEmail && (
          <div className="text-[10px] text-slate-400 mt-0.5" title="Facilitator this device belongs to">{device.ownerEmail}</div>
        )}
      </td>

      {/* Version */}
      <td className="px-4 py-3 whitespace-nowrap">
        {isGlassesDevice(device) && getGlassesMetrics(device).firmware ? (
          <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono">
            {getGlassesMetrics(device).firmware}
          </span>
        ) : metrics?.codeVersion ? (
          <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-mono">
            v{metrics.codeVersion}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* Location */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="flex items-center gap-1.5 text-xs text-slate-500">
          <Monitor size={11} className="text-slate-400" />
          {device.locationId}
        </span>
      </td>

      {/* IP */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Wifi size={11} />
          {device.ipAddress}
        </span>
      </td>
    </>
  );
}

function MetricsCells({ metrics }: { metrics: DeviceMetrics }) {
  return (
    <>
      {/* Uptime */}
      <td className="px-4 py-3 whitespace-nowrap">
        {metrics && metrics.uptimeSeconds >= 0 ? (
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock size={11} className="text-slate-400" />
            {formatUptime(metrics.uptimeSeconds)}
          </span>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>

      {/* CPU / Mem */}
      <td className="px-4 py-3 whitespace-nowrap">
        {metrics ? (
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1">
              <Cpu size={11} className="text-slate-400" />
              <span className={getMetricColor(metrics.cpuPercent)}>
                {metrics.cpuPercent >= 0 ? `${metrics.cpuPercent}%` : '—'}
              </span>
            </span>
            <span className="text-slate-200">/</span>
            <span className="flex items-center gap-1">
              <Activity size={11} className="text-slate-400" />
              <span className={getMetricColor(metrics.memPercent)}>
                {metrics.memPercent >= 0 ? `${metrics.memPercent}%` : '—'}
              </span>
            </span>
          </div>
        ) : (
          <span className="text-slate-300 text-xs">—</span>
        )}
      </td>
    </>
  );
}

interface DeviceRowProps {
  device: DeviceInfo;
  expanded: boolean;
  isDeployTarget: boolean;
  healthRequesting: string | null;
  restartingDevice: string | null;
  loadingHistory: string | null;
  commands: ManagementCommand[];
  onExpand: (deviceId: string) => void;
  onToggleTarget: (deviceId: string) => void;
  onRequestHealth: (deviceId: string) => void;
  onSetConfigDevice: (deviceId: string) => void;
  onRestart: (deviceId: string) => void;
}

function DeviceRow({
  device,
  expanded,
  isDeployTarget,
  healthRequesting,
  restartingDevice,
  loadingHistory,
  commands,
  onExpand,
  onToggleTarget,
  onRequestHealth,
  onSetConfigDevice,
  onRestart,
}: DeviceRowProps) {
  const glasses = isGlassesDevice(device);
  const glassesMetrics = glasses ? getGlassesMetrics(device) : null;
  const metrics = glasses ? null : (device.systemMetrics || null);
  const online = isDeviceOnline(device);
  const isHealthLoading = healthRequesting === device.deviceId;
  const isRestarting = restartingDevice === device.deviceId;
  const isLoadingHistory = loadingHistory === device.deviceId;

  const commandHistoryContent = renderCommandHistory(isLoadingHistory, commands);

  return (
    <React.Fragment>
      <tr
        className="border-b border-[#e2e8f0] hover:bg-slate-50/60 cursor-pointer transition-colors"
        onClick={() => onExpand(device.deviceId)}
      >
        <td className="px-4 py-3 w-10" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isDeployTarget}
            onChange={() => onToggleTarget(device.deviceId)}
            className="rounded accent-primary cursor-pointer"
          />
        </td>

        {/* Status */}
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className={`text-xs font-semibold ${online ? 'text-emerald-700' : 'text-slate-400'}`}>
              {online ? 'Online' : 'Offline'}
            </span>
          </div>
        </td>

        <DeviceIdentityCells device={device} metrics={metrics} />

        <MetricsCells metrics={metrics} />

        {/* Actions */}
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center justify-end gap-1.5" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => onRequestHealth(device.deviceId)}
              disabled={isHealthLoading}
              className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 transition-colors"
              title="Request health report"
            >
              {isHealthLoading ? <Loader size={11} className="animate-spin" /> : <Activity size={11} />}
              Health
            </button>
            <button
              onClick={() => onSetConfigDevice(device.deviceId)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              title="Push config update"
            >
              <Settings size={11} />
              Config
            </button>
            <button
              onClick={() => onRestart(device.deviceId)}
              disabled={isRestarting}
              className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 transition-colors"
              title="Restart device service"
            >
              {isRestarting ? <Loader size={11} className="animate-spin" /> : <RotateCcw size={11} />}
              Restart
            </button>
            {expanded
              ? <ChevronDown size={14} className="text-slate-400 ml-1" />
              : <ChevronRight size={14} className="text-slate-400 ml-1" />}
          </div>
        </td>
      </tr>

      {/* Expanded details row */}
      {expanded && (
        <tr className="border-b border-[#e2e8f0]">
          <td colSpan={11} className="px-6 py-4 bg-slate-50/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <MetricCard label="CPU" value={metrics?.cpuPercent} unit="%" icon={Cpu} color={metrics ? getMetricColor(metrics.cpuPercent) : ''} />
              <MetricCard label="Memory" value={metrics?.memPercent} unit="%" icon={Activity} color={metrics ? getMetricColor(metrics.memPercent) : ''} />
              <MetricCard label="Disk" value={metrics?.diskPercent} unit="%" icon={HardDrive} color={metrics ? getMetricColor(metrics.diskPercent) : ''} />
              <MetricCard label="Temp" value={metrics?.tempC} unit="°C" icon={Thermometer} color={metrics ? getTempColor(metrics.tempC) : ''} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
              <div><span className="text-slate-400">Device Type:</span> <span className="font-medium">{device.deviceType}</span></div>
              <div><span className="text-slate-400">Location:</span> <span className="font-medium">{device.locationId}</span></div>
              <div><span className="text-slate-400">Capabilities:</span> <span className="font-medium">{device.capabilities?.join(', ') || 'N/A'}</span></div>
              <div><span className="text-slate-400">Session:</span> <span className="font-medium">{device.sessionId || 'None'}</span></div>
              {glassesMetrics && (
                <>
                  <div><span className="text-slate-400">Owner:</span> <span className="font-medium">{glassesMetrics.ownerEmail || 'Unknown'}</span></div>
                  <div><span className="text-slate-400">Serial:</span> <span className="font-medium font-mono">{glassesMetrics.serial || 'Unknown'}</span></div>
                  <div><span className="text-slate-400">HUD role:</span> <span className="font-medium">{glassesMetrics.role === 'primary' ? 'Lead' : 'Viewer'}</span></div>
                  <div><span className="text-slate-400">Firmware:</span> <span className="font-medium">{glassesMetrics.firmware || 'Unknown'}</span></div>
                </>
              )}
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Recent Commands</h4>
              {commandHistoryContent}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FleetManagement() {
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Action states
  const [healthRequesting, setHealthRequesting] = useState<string | null>(null);
  const [restartingDevice, setRestartingDevice] = useState<string | null>(null);
  const [bulkRestarting, setBulkRestarting] = useState(false);
  const [expandedDevice, setExpandedDevice] = useState<string | null>(null);
  const [commandHistory, setCommandHistory] = useState<Record<string, ManagementCommand[]>>({});
  const [loadingHistory, setLoadingHistory] = useState<string | null>(null);

  // Config update state
  const [configDevice, setConfigDevice] = useState<string | null>(null);
  const [configJson, setConfigJson] = useState('');
  const [configRequireRestart, setConfigRequireRestart] = useState(false);
  const [pushingConfig, setPushingConfig] = useState(false);

  // Deploy state
  const [showDeploy, setShowDeploy] = useState(false);
  const [deployVersion, setDeployVersion] = useState('');
  const [deployFile, setDeployFile] = useState<File | null>(null);
  const [deployService, setDeployService] = useState<'' | 'camera-capture' | 'audio-capture'>('');
  const [deploying, setDeploying] = useState(false);
  const [deployTargets, setDeployTargets] = useState<Set<string>>(new Set());

  // Table state
  const [sort, setSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<Filters>({ search: '', status: '', deviceType: '', transport: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{ isOpen: boolean; action: 'restart' | 'bulkRestart'; deviceId?: string }>({ isOpen: false, action: 'restart' });

  const fetchDevices = useCallback(async () => {
    try {
      const res = await listDevicesViaN8n();
      setDevices(res.devices || []);
      setError(null);
    } catch (e: any) {
      setError(`Failed to load devices: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    const interval = setInterval(fetchDevices, 30000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  // Derived data
  const deviceTypes = useMemo(() => [...new Set(devices.map(d => d.deviceType))].sort((a, b) => a.localeCompare(b)), [devices]);
  const filtered = useMemo(() => applyFilters(devices, filters), [devices, filters]);
  const sorted = useMemo(() => applySort(filtered, sort), [filtered, sort]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
  const onlineCount = useMemo(() => devices.filter(isDeviceOnline).length, [devices]);

  const hasActiveFilters = filters.search !== '' || filters.status !== '' || filters.deviceType !== '' || filters.transport !== '';

  function handleSort(col: SortCol) {
    setSort(prev => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
    setPage(1);
  }

  function setFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function clearFilters() {
    setFilters({ search: '', status: '', deviceType: '', transport: '' });
    setPage(1);
  }

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  const handleRequestHealth = async (deviceId: string) => {
    setHealthRequesting(deviceId);
    try {
      await requestHealthReport(deviceId);
      showSuccess(`Health report queued for ${deviceId}`);
    } catch (e: any) {
      setError(`Health report failed: ${e.message}`);
    } finally {
      setHealthRequesting(null);
    }
  };

  const handleRestartDevice = (deviceId: string) => {
    setConfirmationModal({ isOpen: true, action: 'restart', deviceId });
  };

  const handleConfirmRestart = async () => {
    if (!confirmationModal.deviceId) return;
    setRestartingDevice(confirmationModal.deviceId);
    try {
      await restartDevice(confirmationModal.deviceId);
      showSuccess(`Restart command queued for ${confirmationModal.deviceId}`);
    } catch (e: any) {
      setError(`Restart failed: ${e.message}`);
    } finally {
      setRestartingDevice(null);
      setConfirmationModal({ isOpen: false, action: 'restart' });
    }
  };

  const handleBulkRestart = () => {
    setConfirmationModal({ isOpen: true, action: 'bulkRestart' });
  };

  const handleConfirmBulkRestart = async () => {
    setBulkRestarting(true);
    const ids = Array.from(deployTargets);
    const errors: string[] = [];
    await Promise.all(ids.map(id =>
      restartDevice(id).catch((e: any) => errors.push(`${id}: ${e.message}`))
    ));
    if (errors.length === 0) {
      showSuccess(`Restart command queued for ${ids.length} device(s)`);
    } else {
      setError(`Restart failed for: ${errors.join(', ')}`);
    }
    setBulkRestarting(false);
    setConfirmationModal({ isOpen: false, action: 'bulkRestart' });
  };

  const handleExpandDevice = async (deviceId: string) => {
    if (expandedDevice === deviceId) {
      setExpandedDevice(null);
      return;
    }
    setExpandedDevice(deviceId);
    if (!commandHistory[deviceId]) {
      setLoadingHistory(deviceId);
      try {
        const res = await getManagementCommands(deviceId, undefined, 10);
        setCommandHistory(prev => ({ ...prev, [deviceId]: res.commands || [] }));
      } catch {
        // ignore
      } finally {
        setLoadingHistory(null);
      }
    }
  };

  const handlePushConfig = async () => {
    if (!configDevice) return;
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(configJson);
    } catch {
      setError('Invalid JSON in config patch');
      return;
    }
    setPushingConfig(true);
    try {
      await pushConfigUpdate(configDevice, parsed, configRequireRestart);
      setConfigDevice(null);
      setConfigJson('');
      setError(null);
    } catch (e: any) {
      setError(`Config push failed: ${e.message}`);
    } finally {
      setPushingConfig(false);
    }
  };

  const deployHasStudioHost = devices.some(d => deployTargets.has(d.deviceId) && d.deviceType === 'studio-server');

  const handleDeploy = async () => {
    if (!deployFile || !deployVersion || deployTargets.size === 0 || (deployHasStudioHost && !deployService)) return;
    setDeploying(true);
    try {
      const { uploadUrl, key, bucket } = await getUploadPackageUrl(deployVersion, deployFile.name);
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: deployFile,
        headers: { 'Content-Type': 'application/gzip' }
      });
      if (!uploadRes.ok) {
        throw new Error(`S3 upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
      }
      const s3Url = `s3://${bucket}/${key}`;
      await deployUpdate(Array.from(deployTargets), s3Url, deployVersion, undefined, deployHasStudioHost ? (deployService as 'camera-capture' | 'audio-capture') : undefined);
      setShowDeploy(false);
      setDeployVersion('');
      setDeployFile(null);
      setDeployService('');
      setDeployTargets(new Set());
      setError(null);
    } catch (e: any) {
      setError(`Deploy failed: ${e.message}`);
    } finally {
      setDeploying(false);
    }
  };

  const toggleDeployTarget = (deviceId: string) => {
    setDeployTargets(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-64 font-display">
        <Loader size={24} className="animate-spin text-slate-400" />
      </div>
    );
  }

  let deviceTableSection: React.ReactNode;
  if (devices.length === 0) {
    deviceTableSection = (
      <div className="text-center py-16 text-slate-400">
        <Server size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">No devices registered</p>
        <p className="text-sm">Devices will appear here once they connect to n8n</p>
      </div>
    );
  } else if (filtered.length === 0) {
    deviceTableSection = (
      <div className="text-center py-12 text-slate-400">
        <p className="text-sm font-medium">No devices match filters</p>
        <button onClick={clearFilters} className="mt-2 text-xs text-indigo-500 hover:underline">Clear filters</button>
      </div>
    );
  } else {
    deviceTableSection = (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50/50 border-b border-[#e2e8f0]">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={paginated.length > 0 && paginated.every(d => deployTargets.has(d.deviceId))}
                  ref={el => {
                    if (el) el.indeterminate = paginated.some(d => deployTargets.has(d.deviceId)) && !paginated.every(d => deployTargets.has(d.deviceId));
                  }}
                  onChange={() => {
                    if (paginated.every(d => deployTargets.has(d.deviceId))) {
                      paginated.forEach(d => toggleDeployTarget(d.deviceId));
                    } else {
                      paginated.filter(d => !deployTargets.has(d.deviceId)).forEach(d => toggleDeployTarget(d.deviceId));
                    }
                  }}
                  className="rounded accent-primary cursor-pointer"
                />
              </th>
              <ColHeader col="status" label="Status" sort={sort} onSort={handleSort} />
              <ColHeader col="deviceId" label="Device ID" sort={sort} onSort={handleSort} />
              <ColHeader col="deviceType" label="Type" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap text-left">Role</th>
              <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap text-left">Version</th>
              <ColHeader col="locationId" label="Location" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap text-left">IP</th>
              <ColHeader col="uptime" label="Uptime" sort={sort} onSort={handleSort} />
              <ColHeader col="cpu" label="CPU / Mem" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 whitespace-nowrap text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(device => (
              <DeviceRow
                key={device.deviceId}
                device={device}
                expanded={expandedDevice === device.deviceId}
                isDeployTarget={deployTargets.has(device.deviceId)}
                healthRequesting={healthRequesting}
                restartingDevice={restartingDevice}
                loadingHistory={loadingHistory}
                commands={commandHistory[device.deviceId] || []}
                onExpand={handleExpandDevice}
                onToggleTarget={toggleDeployTarget}
                onRequestHealth={handleRequestHealth}
                onSetConfigDevice={setConfigDevice}
                onRestart={handleRestartDevice}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Server size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Fleet Management</h1>
            <p className="text-sm text-slate-500 font-medium">
              {devices.length} device{devices.length !== 1 ? 's' : ''} registered
              {' '}&middot;{' '}
              <span className="text-emerald-600">{onlineCount} online</span>
              {devices.length - onlineCount > 0 && (
                <>{' '}&middot;{' '}<span className="text-slate-400">{devices.length - onlineCount} offline</span></>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDeploy(!showDeploy)}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Upload size={16} />
            Deploy Update
          </button>
          <button
            onClick={() => { setLoading(true); fetchDevices(); }}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </header>

      {/* Success banner */}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-6 text-sm text-emerald-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="text-emerald-500 hover:text-emerald-700">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 text-sm text-red-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Deploy panel */}
      {showDeploy && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 shadow-sm">
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-4">Deploy Software Update</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-semibold text-navy block mb-2">Version</label>
              <input
                value={deployVersion}
                onChange={e => setDeployVersion(e.target.value)}
                placeholder="e.g. 1.1.0"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-navy block mb-2">Package (.tar.gz)</label>
              <input
                type="file"
                accept=".tar.gz,.tgz,.gz,application/gzip,application/x-gzip"
                onChange={e => setDeployFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              />
            </div>
          </div>
          {deployHasStudioHost && (
            <div className="mb-4">
              <label className="text-sm font-semibold text-navy block mb-2">Studio service to update</label>
              <select
                value={deployService}
                onChange={e => setDeployService(e.target.value as '' | 'camera-capture' | 'audio-capture')}
                className="w-full max-w-xs border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">Choose a service</option>
                <option value="camera-capture">camera-capture</option>
                <option value="audio-capture">audio-capture</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">Applies to the selected studio servers. The package must be for this service.</p>
            </div>
          )}
          <div className="mb-4">
            <label className="text-sm font-semibold text-navy block mb-2">Target Devices</label>
            <div className="flex flex-wrap gap-2">
              {devices.map(d => (
                <button
                  key={d.deviceId}
                  onClick={() => toggleDeployTarget(d.deviceId)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                    deployTargets.has(d.deviceId)
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40'
                  }`}
                >
                  {d.deviceId}
                </button>
              ))}
              <button
                onClick={() => {
                  if (deployTargets.size === devices.length) setDeployTargets(new Set());
                  else setDeployTargets(new Set(devices.map(d => d.deviceId)));
                }}
                className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 transition-colors"
              >
                {deployTargets.size === devices.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowDeploy(false)}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeploy}
              disabled={deploying || !deployFile || !deployVersion || deployTargets.size === 0 || (deployHasStudioHost && !deployService)}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
            >
              {deploying ? <Loader size={14} className="animate-spin" /> : <Package size={14} />}
              Deploy to {deployTargets.size} device{deployTargets.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Config update modal */}
      {configDevice && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 shadow-sm">
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-4">
            Push Config to {configDevice}
          </p>
          <div className="mb-3">
            <label className="text-sm font-semibold text-navy block mb-2">Config Patch (JSON)</label>
            <textarea
              value={configJson}
              onChange={e => setConfigJson(e.target.value)}
              placeholder='{"captureInterval": 30, "pollInterval": 2}'
              rows={4}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm font-mono text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={configRequireRestart}
                onChange={e => setConfigRequireRestart(e.target.checked)}
                className="rounded accent-primary"
              />
              Require service restart
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setConfigDevice(null); setConfigJson(''); }}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePushConfig}
                disabled={pushingConfig || !configJson.trim()}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 shadow-sm disabled:opacity-50 transition-colors"
              >
                {pushingConfig ? <Loader size={14} className="animate-spin" /> : <Settings size={14} />}
                Push Config
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device table */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        {/* Filter bar */}
        <div className="flex items-center gap-3 p-4 border-b border-[#e2e8f0]">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search device ID or location..."
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-[#e2e8f0] rounded-xl text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filters.status}
            onChange={e => setFilter('status', e.target.value)}
            className="px-3 py-2 text-sm border border-[#e2e8f0] rounded-xl text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            <option value="">All statuses</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
          <select
            value={filters.deviceType}
            onChange={e => setFilter('deviceType', e.target.value)}
            className="px-3 py-2 text-sm border border-[#e2e8f0] rounded-xl text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            <option value="">All types</option>
            {deviceTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={filters.transport}
            onChange={e => setFilter('transport', e.target.value)}
            className="px-3 py-2 text-sm border border-[#e2e8f0] rounded-xl text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
          >
            <option value="">All transports</option>
            <option value="iot">IoT</option>
            <option value="backend">Backend</option>
            <option value="n8n">n8n</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <X size={12} /> Clear
            </button>
          )}
          {deployTargets.size > 0 && (
            <button
              onClick={handleBulkRestart}
              disabled={bulkRestarting}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 transition-colors"
            >
              {bulkRestarting ? <Loader size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Restart Selected ({deployTargets.size})
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">
            {filtered.length} of {devices.length}
          </span>
        </div>

        {deviceTableSection}

        {/* Pagination footer */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#e2e8f0] bg-slate-50/50">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="border border-[#e2e8f0] rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}</span>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="p-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="font-medium">{safePage} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="p-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.action === 'restart' ? 'Restart device?' : 'Restart devices?'}
        message={confirmationModal.action === 'restart'
          ? `Restart service on ${confirmationModal.deviceId}?`
          : `Restart service on ${deployTargets.size} selected device(s)?`
        }
        confirmLabel="Restart"
        onConfirm={confirmationModal.action === 'restart' ? handleConfirmRestart : handleConfirmBulkRestart}
        onCancel={() => setConfirmationModal({ isOpen: false, action: confirmationModal.action })}
        isDestructive={false}
        isLoading={restartingDevice !== null || bulkRestarting}
      />

      </div>
    </div>
  );
}
